package ui.report.device

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import common.frontend.api.IoReport
import io.Repository
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.char
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.printStackTraceInDebug
import ui.report.device.ReportDeviceAliveScope.Companion.DATE_FORMAT
import ui.widget.ErrUI
import ui.widget.LoadingUI
import ui.widget.Space
import kotlin.time.Duration.Companion.seconds


abstract class ReportDeviceAliveScope(val deviceId: Long) {

    abstract val vm: ReportDeviceAliveVM
    abstract val action: (ReportDeviceAliveVM.Action) -> Unit

    companion object {
        val DATE_FORMAT = LocalDateTime.Format {
            year()
            char(' ')
            monthName(MonthNames.ENGLISH_ABBREVIATED)
            char(' ')
            day()
            char(' ')
            char(' ')
            hour()
            char(':')
            minute()
        }

        @Composable
        fun rememberScope(): ReportDeviceAliveScope {
            val args = LocalNavigationArgs.current.rememberArgs(ReportDeviceArgs::class)
            val nav = LocalNavigation.current
            val vm = nav.viewModel(ReportDeviceAliveVM::class, args.deviceId.toString())
            val action = ReportDeviceAliveVM.Action.rememberAction(vm)

            return remember(args) {
                object : ReportDeviceAliveScope(args.deviceId) {
                    override val vm: ReportDeviceAliveVM
                        get() = vm
                    override val action: (ReportDeviceAliveVM.Action) -> Unit
                        get() = action

                }.apply {
                    vm.init(this)
                }
            }
        }

    }
}

@VM
class ReportDeviceAliveVM : NavigationViewModel() {

    private lateinit var scopeUi: ReportDeviceAliveScope

    var state by mutableDataState<List<IoReport.ListingDeviceAlive.Item>>()
        private set

    var canLoadMore by mutableStateOf(false)
        private set

    sealed interface Action {
        data object LoadMore : Action
        companion object {

            @Composable
            fun rememberAction(vm: ReportDeviceAliveVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            LoadMore -> vm.loadMore()
                        }
                    }
                }
            }
        }
    }

    private fun loadMore() {
        canLoadMore = false
        scope.launch {
            state.entry?.also { oldItems ->
                runCatching {
                    val items = Repository.api.report.listingDeviceAlive(scopeUi.deviceId, 0).items
                    state = DataState.Result(oldItems + items)
                    canLoadMore = items.size == 100
                }.onFailure {
                    it.printStackTraceInDebug()
                    state = DataState.Error(it.message!!, throwable = it)
                }
            }

        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

    fun init(scopeUi: ReportDeviceAliveScope) {
        this.scopeUi = scopeUi
        if (state !is DataState.Result) {
            state = DataState.Loading()
        }
        scope.launch {
            runCatching {
                val items = Repository.api.report.listingDeviceAlive(scopeUi.deviceId, 0).items
                state = DataState.Result(items)
                canLoadMore = items.size == 100
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }
}


@Composable
@ComposableNavNode(ReportDeviceArgs::class)
fun ReportDeviceAliveUI() {
    with(ReportDeviceAliveScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun ReportDeviceAliveScope.Content(nnvl: List<IoReport.ListingDeviceAlive.Item>) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        contentPadding = PaddingValues(16.dp)
    ) {
        itemsIndexed(nnvl) { index, item ->
            Item(index, item)
        }
        if (vm.canLoadMore) {
            item {
                Box(Modifier.fillMaxWidth()) {
                    Button(
                        onClick = { action(ReportDeviceAliveVM.Action.LoadMore) },
                        modifier = Modifier.align(Alignment.Center)
                            .padding(16.dp)
                    ) {
                        Text("LOAD MORE")
                    }
                }
            }
        }
    }
}

@Composable
fun ReportDeviceAliveScope.Item(index: Int, item: IoReport.ListingDeviceAlive.Item) {
    Card(modifier = Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth().padding(8.dp)) {
            val dt = remember(item.id) {
                item.utc.toInstant(TimeZone.UTC).toLocalDateTime(TimeZone.currentSystemDefault())
            }
            val name = remember(item.id) {
                dt.format(DATE_FORMAT)
            }
            if (item.on) {
                Text(
                    text = "ON",
                    modifier = Modifier.width(64.dp),
                    fontWeight = FontWeight.ExtraBold
                )
            } else {
                Text(
                    text = "OFF",
                    modifier = Modifier.width(64.dp),
                    fontWeight = FontWeight.Medium
                )
            }
            Text(name)
            if (index == 0) {
                Space(16.dp)
                Text("( ${(kotlin.time.Clock.System.now() - dt.toInstant(TimeZone.currentSystemDefault())).inWholeSeconds.seconds} )")
            }
        }
    }
}
